import { CHANNELS } from "../../shared/channels";
import {
  LatLng,
  MAP_CATEGORIES,
  MAP_HOME,
  MAP_RADII,
  MapPlace,
  MapsConfig,
  MapsState,
  PIN_COLOURS,
  PIN_PREFIX,
  RoutePlan,
  RouteStop,
  SavedPlace,
  SavedRoute,
  TRAVEL_MODES,
  TravelMode,
  directionsLink,
  distanceText,
  durationText,
  isPin,
  metresBetween,
  parkingText
} from "../../shared/maps";
import { call, listen } from "../bridge";
import { byId, cloneTemplate, fillFields, flashToast, renderList } from "../dom";
import { onViewShown, showView } from "../navigation";
import { onSharedChange, readSharedAll, sharedHead } from "../shares";
import { amOwner } from "../me";
import { languageById } from "../../shared/i18n";
import { currentLanguage, locale, t } from "../i18n";
import { scrollBehavior } from "../motion_pref";

// the map: what's around with its rating, reviews and parking, and routes through a few stops.
// google answers through the server, the map tiles load straight from google with the browser key

type Mode = "nearby" | "route" | "saved";
type Sort = "rating" | "reviews" | "distance" | "parking";
type Where = LatLng & { label: string };

// google's map script has no types here, kept to the few calls below
type Google = any;

declare global {
  interface Window {
    google?: Google;
    chiMapsReady?: () => void;
    gm_authFailure?: () => void;
  }
}

const EMPTY: MapsState = { places: [], routes: [] };
const KEEP = "chix-maps";
const STOP_LETTERS = "ABCDEFGHIJ";

let config: MapsConfig | null = null;
let mode: Mode = "nearby";
let centre: Where = { ...MAP_HOME };
let me: LatLng | null = null;
let category = "";
let query = "";
let radius = 3000;
let sort: Sort = "rating";
let results: MapPlace[] = [];
let searching = false;
let stops: RouteStop[] = [];
let travel: TravelMode = "drive";
let plan: RoutePlan | null = null;
let saved: MapsState = EMPTY;
let shared: Array<{ id: string; name: string; data: MapsState }> = [];
// tapping the map drops a pin while this is on. draft is the pin being named or edited
let dropping = false;
let draft: { id: string; location: LatLng; colour: string } | null = null;

let google: Google = null;
let map: Google = null;
let pins: Google[] = [];
let line: Google = null;
let mapLoading: Promise<boolean> | null = null;

function oops(error: unknown): void {
  flashToast(error instanceof Error ? error.message.replace(/^.*Error: /, "") : t("couldn't do that"));
}

// the planner and the last search come back next time on this screen only
function keep(): void {
  try {
    localStorage.setItem(KEEP, JSON.stringify({ stops, travel, radius, category }));
  } catch {
    // private mode
  }
}

function restore(): void {
  try {
    const kept = JSON.parse(localStorage.getItem(KEEP) || "{}") as { stops?: RouteStop[]; travel?: TravelMode; radius?: number; category?: string };
    stops = Array.isArray(kept.stops) ? kept.stops.slice(0, 10) : [];
    travel = TRAVEL_MODES.some((item) => item.id === kept.travel) ? kept.travel! : "drive";
    radius = MAP_RADII.includes(Number(kept.radius)) ? Number(kept.radius) : 3000;
    category = MAP_CATEGORIES.some((item) => item.id === kept.category) ? String(kept.category) : "";
  } catch {
    // nothing kept
  }
}

// ---------- the map itself ----------

// css colours to the #rrggbb google's styles want, see-through ones laid over the page
function cssColour(name: string, over = "#000000"): string {
  const probe = document.createElement("i");
  probe.style.color = `var(${name})`;
  document.body.append(probe);
  const value = getComputedStyle(probe).color;
  probe.remove();
  const parts = value.match(/[\d.]+/g)?.map(Number) || [0, 0, 0, 1];
  const alpha = parts[3] ?? 1;
  const base = over.match(/\w\w/g)!.map((hex) => parseInt(hex, 16));
  return `#${parts.slice(0, 3).map((part, at) => Math.round(part * alpha + base[at] * (1 - alpha)).toString(16).padStart(2, "0")).join("")}`;
}

function mix(a: string, b: string, amount: number): string {
  const one = a.match(/\w\w/g)!.map((hex) => parseInt(hex, 16));
  const two = b.match(/\w\w/g)!.map((hex) => parseInt(hex, 16));
  return `#${one.map((part, at) => Math.round(part + (two[at] - part) * amount).toString(16).padStart(2, "0")).join("")}`;
}

// the map in the theme's own colours: the page for land, the well for water, roads drawn in ink
function mapStyles(): unknown[] {
  const bg = cssColour("--panel-solid");
  const ink = cssColour("--ink", bg);
  const well = cssColour("--well", bg);
  const chi = cssColour("--chi", bg);
  return [
    { elementType: "geometry", stylers: [{ color: bg }] },
    { elementType: "labels.text.fill", stylers: [{ color: mix(bg, ink, 0.62) }] },
    { elementType: "labels.text.stroke", stylers: [{ color: bg }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: well }] },
    { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: mix(bg, chi, 0.05) }] },
    { featureType: "poi", stylers: [{ visibility: "off" }] },
    { featureType: "poi.park", elementType: "geometry", stylers: [{ visibility: "on" }, { color: mix(bg, cssColour("--green", bg), 0.12) }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: mix(bg, ink, 0.14) }] },
    { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: mix(bg, ink, 0.22) }] },
    { featureType: "road.highway", elementType: "geometry", stylers: [{ color: mix(bg, ink, 0.34) }] },
    { featureType: "road", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
    { featureType: "transit", elementType: "geometry", stylers: [{ color: mix(bg, ink, 0.18) }] },
    { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: mix(bg, ink, 0.3) }] }
  ];
}

function canvasNote(text: string): void {
  const note = byId("maps-canvas-note");
  note.textContent = text;
  note.hidden = !text;
  byId("maps-frame").classList.toggle("off", Boolean(text));
}

function loadGoogle(key: string): Promise<boolean> {
  if (mapLoading) return mapLoading;
  mapLoading = new Promise((done) => {
    if (window.google?.maps) return done(true);
    window.chiMapsReady = () => done(true);
    window.gm_authFailure = () => {
      canvasNote(t("google turned the map key away. check it's allowed on this site and maps javascript api is on"));
      done(false);
    };
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&v=weekly&libraries=geometry&loading=async&callback=chiMapsReady&region=GB&language=${encodeURIComponent(languageById(currentLanguage()).locale)}`;
    script.async = true;
    script.onerror = () => done(false);
    document.head.append(script);
  });
  return mapLoading;
}

async function bootMap(): Promise<void> {
  if (map || !config?.browserKey) {
    if (!config?.browserKey) canvasNote(t("the map needs a key to draw, searches and routes still work below"));
    return;
  }
  canvasNote("");
  const ok = await loadGoogle(config.browserKey);
  if (!ok || !window.google?.maps) {
    if (byId("maps-canvas-note").hidden) canvasNote(t("the map couldn't load. searches and routes still work below"));
    return;
  }
  google = window.google;
  map = new google.maps.Map(byId("maps-canvas"), {
    center: { lat: centre.lat, lng: centre.lng },
    zoom: 13,
    styles: mapStyles(),
    backgroundColor: cssColour("--panel-solid"),
    disableDefaultUI: true,
    zoomControl: true,
    clickableIcons: false,
    gestureHandling: "greedy",
    keyboardShortcuts: false
  });
  // moving the map offers to look again where it now is
  map.addListener("dragend", () => {
    byId("maps-here").hidden = mode !== "nearby";
  });
  map.addListener("click", (event: Google) => {
    if (!dropping || !event.latLng) return;
    setDropping(false);
    openPin({ id: PIN_PREFIX, location: { lat: event.latLng.lat(), lng: event.latLng.lng() }, colour: PIN_COLOURS[0] });
  });
  // a new theme repaints the map in it
  new MutationObserver(() => map?.setOptions({ styles: mapStyles(), backgroundColor: cssColour("--panel-solid") }))
    .observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
  paintPins();
}

function dot(colour: string, scale = 9) {
  return { path: google.maps.SymbolPath.CIRCLE, scale, fillColor: colour, fillOpacity: 1, strokeColor: cssColour("--panel-solid"), strokeWeight: 2 };
}

function pin(at: LatLng, title: string, colour: string, text = "", onTap?: () => void) {
  const marker = new google.maps.Marker({
    map,
    position: at,
    title,
    icon: dot(colour, text ? 11 : 7),
    label: text ? { text, color: cssColour("--chi-ink"), fontSize: "11px", fontWeight: "700" } : undefined
  });
  if (onTap) marker.addListener("click", onTap);
  pins.push(marker);
}

function flashCard(id: string): void {
  const card = document.querySelector<HTMLElement>(`[data-place-id="${CSS.escape(id)}"]`);
  if (!card) return;
  card.scrollIntoView({ behavior: scrollBehavior(), block: "center" });
  card.classList.remove("lit");
  void card.offsetWidth;
  card.classList.add("lit");
}

// what's on the map follows the pane: results, the route, or saved places
function paintPins(): void {
  if (!map) return;
  pins.forEach((marker) => marker.setMap(null));
  pins = [];
  line?.setMap(null);
  line = null;
  const bounds = new google.maps.LatLngBounds();
  const add = (at: LatLng) => bounds.extend(at);
  if (me) pin(me, t("you"), cssColour("--green"));
  // my own pins and the ones shared with me are landmarks, on the map whatever pane is open
  [...saved.places, ...shared.flatMap((person) => person.data.places)].filter(isPin).forEach((spot) => {
    if (draft?.id === spot.id) return;
    pin(spot.location, spot.name, pinColour(spot.colour), "", () => {
      const mine = saved.places.some((item) => item.id === spot.id);
      if (mine) openPin({ id: spot.id, location: spot.location, colour: spot.colour || PIN_COLOURS[0] }, spot.name, spot.note);
      else {
        setMode("saved");
        flashCard(spot.id);
      }
    });
  });
  if (draft) pin(draft.location, t("new pin"), pinColour(draft.colour), "+");
  if (mode === "nearby") {
    sorted().forEach((place, at) => {
      pin(place.location, place.name, cssColour("--chi"), String(at + 1), () => flashCard(place.id));
      add(place.location);
    });
  }
  if (mode === "route") {
    const shown = plan?.stops || stops;
    shown.forEach((stop, at) => {
      if (!stop.location) return;
      pin(stop.location, stop.label, cssColour("--amber"), STOP_LETTERS[at]);
      add(stop.location);
    });
    if (plan?.polyline && google.maps.geometry) {
      const path = google.maps.geometry.encoding.decodePath(plan.polyline);
      line = new google.maps.Polyline({ map, path, strokeColor: cssColour("--chi"), strokeOpacity: 0.9, strokeWeight: 5 });
      path.forEach((point: Google) => bounds.extend(point));
    }
  }
  if (mode === "saved") {
    [...saved.places, ...shared.flatMap((person) => person.data.places)].filter((place) => !isPin(place)).forEach((place) => {
      pin(place.location, place.name, cssColour("--violet"), "★", () => flashCard(place.id));
      add(place.location);
    });
  }
  if (!bounds.isEmpty()) {
    map.fitBounds(bounds, 48);
    if (map.getZoom() > 16) map.setZoom(16);
  } else {
    map.panTo({ lat: centre.lat, lng: centre.lng });
  }
}

// ---------- nearby ----------

function sorted(): MapPlace[] {
  const parkingRank = (place: MapPlace) => place.parking === null ? 1 : place.parking.length ? 0 : 2;
  return [...results].sort((a, b) => {
    if (sort === "distance") return a.distance - b.distance;
    if (sort === "reviews") return b.reviews - a.reviews;
    if (sort === "parking" && parkingRank(a) !== parkingRank(b)) return parkingRank(a) - parkingRank(b);
    return b.rating - a.rating || b.reviews - a.reviews;
  });
}

function isSaved(id: string): boolean {
  return saved.places.some((place) => place.id === id);
}

function placeMeta(place: MapPlace): string {
  const open = place.openNow === null ? "" : place.openNow ? t("open now") : t("closed now");
  return [isPin(place) ? "" : place.kind, distanceText(place.distance), open].filter(Boolean).join(" · ");
}

// readOnly: someone else's saved place, it can go on my route but isn't mine to unsave
function placeCard(place: MapPlace | SavedPlace, readOnly = false): HTMLElement {
  const note = "note" in place ? place.note : "";
  const card = fillFields(cloneTemplate("template-place"), {
    score: place.rating ? place.rating.toFixed(1) : t("new"),
    name: place.name,
    meta: [placeMeta(place), note].filter(Boolean).join(" · "),
    address: place.address,
    reviews: place.reviews
      ? place.reviews === 1
        ? t("{rating} from 1 review", { rating: place.rating.toFixed(1) })
        : t("{rating} from {count} reviews", { rating: place.rating.toFixed(1), count: place.reviews.toLocaleString(locale()) })
      : t("no reviews yet"),
    parking: t(parkingText(place.parking))
  });
  card.dataset.placeId = place.id;
  const pinned = isPin(place);
  if (pinned) {
    card.classList.add("is-pin");
    card.style.setProperty("--swatch", pinColour("colour" in place ? place.colour : ""));
    fillFields(card, { score: t("pin") });
  }
  card.querySelector(".place-rating")!.classList.toggle("high", place.rating >= 4.5);
  card.querySelector("[data-fact='parking']")!.classList.add(place.parking === null ? "unknown" : place.parking.length ? "yes" : "no");
  card.addEventListener("click", () => {
    if (map) {
      map.panTo(place.location);
      map.setZoom(Math.max(map.getZoom(), 15));
      byId("maps-frame").scrollIntoView({ behavior: scrollBehavior(), block: "nearest" });
    }
  });
  const key = (action: string) => card.querySelector<HTMLButtonElement>(`[data-action='${action}']`)!;
  const tap = (action: string, run: () => void) => key(action).addEventListener("click", (event) => {
    event.stopPropagation();
    run();
  });
  tap("route", () => {
    addStop({ label: place.name, placeId: place.id, location: place.location });
    flashToast(t("{place} is stop {letter}", { place: place.name, letter: STOP_LETTERS[stops.length - 1] }));
  });
  const edit = key("edit");
  if (pinned && !readOnly) {
    key("save").remove();
    edit.hidden = false;
    tap("edit", () => openPin({ id: place.id, location: place.location, colour: ("colour" in place && place.colour) || PIN_COLOURS[0] }, place.name, note));
  } else edit.remove();
  if (readOnly) key("save").remove();
  else if (!pinned) {
    const on = isSaved(place.id);
    key("save").classList.toggle("accent", on);
    key("save").querySelector("span")!.textContent = on ? t("saved") : t("save");
    tap("save", () => void toggleSaved(place));
  }
  tap("parking", () => {
    centre = { ...place.location, label: place.name };
    category = "parking";
    query = "";
    radius = 1000;
    byId<HTMLInputElement>("maps-query").value = "";
    setMode("nearby");
    void search();
  });
  if (place.mapsUrl) tap("open", () => void call(CHANNELS.openExternal, place.mapsUrl));
  else key("open").remove();
  return card;
}

function paintResults(): void {
  const list = sorted();
  renderList(byId("maps-results"), list, (place) => placeCard(place));
  const empty = byId("maps-results-empty");
  empty.hidden = list.length > 0;
  empty.textContent = searching ? t("looking…") : category || query ? t("nothing found there. try further out or other words") : t("pick something to look for, or search above");
  const label = MAP_CATEGORIES.find((item) => item.id === category)?.label;
  const what = query ? `"${query}"` : label ? t(label) : "";
  byId("maps-where").textContent = what
    ? t("{what} within {distance} of {place}", { what, distance: distanceText(radius), place: centre.label })
    : t("around {place}", { place: centre.label });
  byId("maps-cats").querySelectorAll<HTMLElement>("[data-cat]").forEach((chip) => chip.classList.toggle("active", !query && chip.dataset.cat === category));
  byId("maps-sort").querySelectorAll<HTMLElement>("[data-sort]").forEach((chip) => chip.classList.toggle("active", chip.dataset.sort === sort));
  byId<HTMLSelectElement>("maps-radius").value = String(radius);
  if (mode === "nearby") paintPins();
}

async function search(): Promise<void> {
  if (!config?.ready || (!category && !query)) return;
  searching = true;
  results = [];
  paintResults();
  byId("maps-here").hidden = true;
  try {
    results = await call<MapPlace[]>(CHANNELS.mapsNearby, { centre, category, query, radius });
  } catch (error) {
    oops(error);
  }
  searching = false;
  keep();
  paintResults();
}

// ---------- route ----------

function addStop(stop: RouteStop): void {
  if (stops.length >= 10) return flashToast(t("ten stops is the most"));
  stops = [...stops, stop];
  plan = null;
  keep();
  paintRoute();
}

function moveStop(from: number, to: number): void {
  if (to < 0 || to >= stops.length) return;
  const next = [...stops];
  const [stop] = next.splice(from, 1);
  next.splice(to, 0, stop);
  stops = next;
  plan = null;
  keep();
  paintRoute();
}

function paintRoute(): void {
  const shown = plan?.stops || stops;
  renderList(byId("maps-stops"), shown.map((stop, at) => ({ stop, at })), ({ stop, at }) => {
    const row = fillFields(cloneTemplate("template-stop"), { letter: STOP_LETTERS[at], label: stop.label });
    const tap = (action: string, run: () => void) => row.querySelector(`[data-action='${action}']`)!.addEventListener("click", (event) => {
      event.stopPropagation();
      if (plan) stops = plan.stops;
      run();
    });
    row.querySelector<HTMLButtonElement>("[data-action='up']")!.disabled = at === 0;
    row.querySelector<HTMLButtonElement>("[data-action='down']")!.disabled = at === shown.length - 1;
    tap("up", () => moveStop(at, at - 1));
    tap("down", () => moveStop(at, at + 1));
    tap("remove", () => {
      stops = stops.filter((_, index) => index !== at);
      plan = null;
      keep();
      paintRoute();
    });
    return row;
  });
  byId("maps-stops-empty").hidden = shown.length > 0;
  byId("maps-travel").querySelectorAll<HTMLElement>("[data-travel]").forEach((key) => key.classList.toggle("active", key.dataset.travel === travel));
  byId<HTMLButtonElement>("maps-plan").disabled = shown.length < 2;
  byId<HTMLButtonElement>("maps-go").disabled = shown.length < 2;
  byId<HTMLButtonElement>("maps-save-route").disabled = shown.length < 2;
  byId<HTMLButtonElement>("maps-clear-route").disabled = !shown.length;
  const box = byId("maps-plan-box");
  box.hidden = !plan;
  if (plan) {
    byId("maps-plan-time").textContent = durationText(plan.duration);
    byId("maps-plan-distance").textContent = distanceText(plan.distance) || "-";
    byId("maps-plan-stops").textContent = String(plan.stops.length);
    byId("maps-legs").replaceChildren(...plan.legs.map((leg, at) => {
      const item = document.createElement("li");
      const letters = document.createElement("b");
      letters.textContent = `${STOP_LETTERS[at]} → ${STOP_LETTERS[at + 1]}`;
      const text = document.createElement("span");
      text.textContent = `${leg.to} · ${durationText(leg.duration)} · ${distanceText(leg.distance)}`;
      item.append(letters, text);
      return item;
    }));
    byId("maps-plan-note").textContent = plan.warnings.join(". ");
  }
  if (mode === "route") paintPins();
}

async function planIt(): Promise<void> {
  if (stops.length < 2) return;
  const button = byId<HTMLButtonElement>("maps-plan");
  button.disabled = true;
  try {
    plan = await call<RoutePlan>(CHANNELS.mapsRoute, stops, travel, byId<HTMLInputElement>("maps-best-order").checked);
    stops = plan.stops;
    keep();
  } catch (error) {
    oops(error);
  }
  paintRoute();
}

function locate(): Promise<LatLng | null> {
  if (!navigator.geolocation) return Promise.resolve(null);
  return new Promise((done) => navigator.geolocation.getCurrentPosition(
    (spot) => done({ lat: spot.coords.latitude, lng: spot.coords.longitude }),
    () => done(null),
    { enableHighAccuracy: true, timeout: 12_000, maximumAge: 60_000 }
  ));
}

// ---------- saved ----------

async function toggleSaved(place: MapPlace): Promise<void> {
  try {
    saved = isSaved(place.id)
      ? await call<MapsState>(CHANNELS.mapsRemovePlace, place.id)
      : await call<MapsState>(CHANNELS.mapsSavePlace, place);
    flashToast(isSaved(place.id) ? t("saved") : t("taken out of saved"));
    paintAll();
  } catch (error) {
    oops(error);
  }
}

function routeCard(route: SavedRoute, readOnly = false): HTMLElement {
  const card = fillFields(cloneTemplate("template-route"), {
    mode: t(TRAVEL_MODES.find((item) => item.id === route.mode)?.label || route.mode),
    name: route.name,
    meta: route.stops.map((stop, at) => `${STOP_LETTERS[at]} ${stop.label}`).join(" · ")
  });
  const load = () => {
    stops = route.stops.map((stop) => ({ ...stop }));
    travel = route.mode;
    plan = null;
    keep();
    setMode("route");
    void planIt();
  };
  card.addEventListener("click", load);
  card.querySelector("[data-action='load']")!.addEventListener("click", (event) => {
    event.stopPropagation();
    load();
  });
  const remove = card.querySelector("[data-action='remove']")!;
  if (readOnly) remove.remove();
  else remove.addEventListener("click", async (event) => {
    event.stopPropagation();
    if (!confirm(t("remove {name}?", { name: route.name }))) return;
    try {
      saved = await call<MapsState>(CHANNELS.mapsRemoveRoute, route.id);
      paintSaved();
    } catch (error) {
      oops(error);
    }
  });
  return card;
}

function paintSaved(): void {
  const here = me || centre;
  const withDistance = (place: SavedPlace) => ({ ...place, distance: metresBetween(here, place.location) });
  const pins = saved.places.filter(isPin);
  const places = saved.places.filter((place) => !isPin(place));
  renderList(byId("maps-pins"), pins.map(withDistance), (place) => placeCard(place));
  byId("maps-pins-empty").hidden = pins.length > 0;
  renderList(byId("maps-saved-places"), places.map(withDistance), (place) => placeCard(place));
  byId("maps-saved-places-empty").hidden = places.length > 0;
  renderList(byId("maps-saved-routes"), saved.routes, (route) => routeCard(route));
  byId("maps-saved-routes-empty").hidden = saved.routes.length > 0;
  const box = byId("maps-shared");
  box.hidden = !shared.length;
  box.replaceChildren(...shared.map((person) => {
    const section = document.createElement("div");
    section.className = "stack shared-box";
    const places = document.createElement("ul");
    places.className = "card-list";
    renderList(places, person.data.places.map(withDistance), (place) => placeCard(place, true));
    const routes = document.createElement("ul");
    routes.className = "card-list";
    renderList(routes, person.data.routes, (route) => routeCard(route, true));
    section.append(sharedHead(person.name, "places"), places, routes);
    if (!person.data.places.length && !person.data.routes.length) {
      const empty = document.createElement("p");
      empty.className = "empty";
      empty.textContent = t("nothing saved yet");
      section.append(empty);
    }
    return section;
  }));
  if (mode === "saved") paintPins();
}

async function loadShared(): Promise<void> {
  shared = await readSharedAll<MapsState>("places", EMPTY);
  paintSaved();
}

// ---------- my own pins ----------

function pinColour(colour = ""): string {
  return cssColour(`--${PIN_COLOURS.includes(colour) ? colour : PIN_COLOURS[0]}`);
}

function setDropping(on: boolean): void {
  dropping = on;
  const key = byId("maps-drop");
  key.setAttribute("aria-pressed", String(on));
  key.querySelector("span")!.textContent = on ? t("tap the map") : t("drop a pin");
  byId("maps-frame").classList.toggle("dropping", on);
}

function paintColours(): void {
  byId("maps-pin-colours").querySelectorAll<HTMLElement>("[data-colour]").forEach((swatch) => swatch.setAttribute("aria-checked", String(swatch.dataset.colour === draft?.colour)));
}

// the pin form under the map, for a new pin (id is just the prefix) or one of mine
function openPin(next: { id: string; location: LatLng; colour: string }, name = "", note = ""): void {
  draft = next;
  const isNew = next.id === PIN_PREFIX;
  byId("maps-pin-title").textContent = isNew ? t("new pin") : t("edit {name}", { name });
  byId<HTMLInputElement>("maps-pin-name").value = name;
  byId<HTMLInputElement>("maps-pin-note").value = note;
  byId("maps-pin-save").textContent = isNew ? t("save pin") : t("save");
  byId("maps-pin-remove").hidden = isNew;
  byId("maps-pin-form").hidden = false;
  paintColours();
  paintPins();
  byId("maps-pin-form").scrollIntoView({ behavior: scrollBehavior(), block: "nearest" });
  if (isNew) byId<HTMLInputElement>("maps-pin-name").focus();
}

function closePin(): void {
  draft = null;
  byId("maps-pin-form").hidden = true;
  paintPins();
}

function pinStop(): RouteStop | null {
  if (!draft) return null;
  const name = byId<HTMLInputElement>("maps-pin-name").value.trim() || t("my pin");
  return { label: name, location: draft.location };
}

async function savePin(): Promise<void> {
  if (!draft) return;
  try {
    saved = await call<MapsState>(CHANNELS.mapsSavePlace, {
      id: draft.id,
      name: byId<HTMLInputElement>("maps-pin-name").value.trim() || t("my pin"),
      note: byId<HTMLInputElement>("maps-pin-note").value,
      colour: draft.colour,
      location: draft.location
    });
    flashToast(draft.id === PIN_PREFIX ? t("pinned") : t("saved"));
    closePin();
    paintAll();
  } catch (error) {
    oops(error);
  }
}

// ---------- the whole place ----------

function setMode(next: Mode): void {
  mode = next;
  byId("maps-mode").querySelectorAll<HTMLElement>("[data-mode]").forEach((key) => {
    key.classList.toggle("active", key.dataset.mode === mode);
    key.setAttribute("aria-selected", String(key.dataset.mode === mode));
  });
  (["nearby", "route", "saved"] as Mode[]).forEach((pane) => {
    byId(`maps-pane-${pane}`).hidden = pane !== mode;
  });
  byId("maps-here").hidden = true;
  paintAll();
}

// each pane repaints the map when it's the one showing
function paintAll(): void {
  paintResults();
  paintRoute();
  paintSaved();
}

function paintSetup(): void {
  const ready = Boolean(config?.ready);
  byId("maps-setup").hidden = ready;
  byId("maps-setup-text").textContent = amOwner()
    ? t("places, ratings, parking and routes come from google maps. it needs a key from your google cloud account, google gives a free allowance each month")
    : t("fractal hasn't added a google maps key yet, so the map can't look anything up");
  byId("maps-frame").hidden = !ready;
  byId("maps-mode").hidden = !ready;
  (["nearby", "route", "saved"] as Mode[]).forEach((pane) => {
    byId(`maps-pane-${pane}`).hidden = !ready || pane !== mode;
  });
}

function buildKeys(): void {
  byId("maps-cats").replaceChildren(...MAP_CATEGORIES.map((item) => {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "chip";
    chip.dataset.cat = item.id;
    chip.textContent = t(item.label);
    return chip;
  }));
  byId("maps-radius").replaceChildren(...MAP_RADII.map((metres) => Object.assign(document.createElement("option"), { value: String(metres), textContent: t("within {distance}", { distance: distanceText(metres) }) })));
  byId("maps-travel").replaceChildren(...TRAVEL_MODES.map((item) => {
    const key = document.createElement("button");
    key.type = "button";
    key.className = "seg-option";
    key.dataset.travel = item.id;
    key.textContent = t(item.label);
    return key;
  }));
}

export function setupMapsView(): void {
  restore();
  buildKeys();
  byId("maps-mode").addEventListener("click", (event) => {
    const key = (event.target as Element).closest<HTMLElement>("[data-mode]");
    if (key) setMode(key.dataset.mode as Mode);
  });
  byId("maps-cats").addEventListener("click", (event) => {
    const chip = (event.target as Element).closest<HTMLElement>("[data-cat]");
    if (!chip) return;
    category = chip.dataset.cat || "";
    query = "";
    byId<HTMLInputElement>("maps-query").value = "";
    void search();
  });
  byId("maps-sort").addEventListener("click", (event) => {
    const chip = (event.target as Element).closest<HTMLElement>("[data-sort]");
    if (!chip) return;
    sort = chip.dataset.sort as Sort;
    paintResults();
  });
  byId("maps-search").addEventListener("submit", (event) => {
    event.preventDefault();
    query = byId<HTMLInputElement>("maps-query").value.trim();
    if (query) category = "";
    void search();
  });
  byId("maps-radius").addEventListener("change", () => {
    radius = Number(byId<HTMLSelectElement>("maps-radius").value) || 3000;
    void search();
  });
  byId("maps-here").addEventListener("click", () => {
    const at = map?.getCenter();
    if (!at) return;
    centre = { lat: at.lat(), lng: at.lng(), label: t("this area") };
    void search();
  });
  byId("maps-locate").addEventListener("click", async () => {
    const found = await locate();
    if (!found) return flashToast(t("couldn't find where you are, staying around {place}", { place: centre.label }));
    me = found;
    centre = { ...found, label: t("you") };
    if (mode !== "nearby") setMode("nearby");
    if (category || query) void search();
    else paintAll();
  });
  byId("maps-open-setup").addEventListener("click", () => showView("settings", "sys"));

  byId("maps-pin-colours").replaceChildren(...PIN_COLOURS.map((colour) => {
    const swatch = document.createElement("button");
    swatch.type = "button";
    swatch.className = "pin-colour";
    swatch.dataset.colour = colour;
    swatch.setAttribute("role", "radio");
    swatch.setAttribute("aria-label", t(colour === "chi" ? "cyan" : colour));
    swatch.style.setProperty("--swatch", `var(--${colour})`);
    swatch.addEventListener("click", () => {
      if (!draft) return;
      draft.colour = colour;
      paintColours();
      paintPins();
    });
    return swatch;
  }));
  byId("maps-drop").addEventListener("click", () => {
    setDropping(!dropping);
    if (dropping) flashToast(t("tap the map where the pin goes"));
  });
  byId("maps-pin-me").addEventListener("click", async () => {
    const found = await locate();
    if (!found) return flashToast("couldn't find where you are");
    me = found;
    openPin({ id: PIN_PREFIX, location: found, colour: PIN_COLOURS[0] });
  });
  byId("maps-pin-form").addEventListener("submit", (event) => {
    event.preventDefault();
    void savePin();
  });
  byId("maps-pin-cancel").addEventListener("click", closePin);
  byId("maps-pin-to").addEventListener("click", () => {
    const stop = pinStop();
    if (!stop) return;
    if (plan) stops = plan.stops;
    addStop(stop);
    closePin();
    setMode("route");
  });
  byId("maps-pin-from").addEventListener("click", () => {
    const stop = pinStop();
    if (!stop) return;
    if (plan) stops = plan.stops;
    stops = [stop, ...stops].slice(0, 10);
    plan = null;
    keep();
    closePin();
    setMode("route");
  });
  byId("maps-pin-remove").addEventListener("click", async () => {
    if (!draft || !confirm(t("take this pin off the map?"))) return;
    try {
      saved = await call<MapsState>(CHANNELS.mapsRemovePlace, draft.id);
      closePin();
      paintAll();
    } catch (error) {
      oops(error);
    }
  });

  byId("maps-stop-form").addEventListener("submit", (event) => {
    event.preventDefault();
    const field = byId<HTMLInputElement>("maps-stop");
    const text = field.value.trim();
    if (!text) return field.focus();
    if (plan) stops = plan.stops;
    addStop({ label: text, address: text });
    field.value = "";
  });
  byId("maps-stop-me").addEventListener("click", async () => {
    const found = await locate();
    if (!found) return flashToast("couldn't find where you are");
    me = found;
    if (plan) stops = plan.stops;
    // where you are is where a route starts
    const here = t("where i am");
    stops = [{ label: here, location: found }, ...stops.filter((stop) => stop.label !== here && stop.label !== "where i am")].slice(0, 10);
    plan = null;
    keep();
    paintRoute();
  });
  byId("maps-stop-home").addEventListener("click", () => {
    if (plan) stops = plan.stops;
    addStop({ label: t("home ({place})", { place: MAP_HOME.label }), location: { lat: MAP_HOME.lat, lng: MAP_HOME.lng } });
  });
  byId("maps-travel").addEventListener("click", (event) => {
    const key = (event.target as Element).closest<HTMLElement>("[data-travel]");
    if (!key) return;
    travel = key.dataset.travel as TravelMode;
    if (plan) stops = plan.stops;
    plan = null;
    keep();
    paintRoute();
  });
  byId("maps-plan").addEventListener("click", () => void planIt());
  byId("maps-go").addEventListener("click", () => {
    const link = directionsLink(plan?.stops || stops, travel);
    if (link) void call(CHANNELS.openExternal, link);
  });
  byId("maps-save-route").addEventListener("click", async () => {
    const route = plan?.stops || stops;
    if (route.length < 2) return;
    const name = prompt(t("name this route"), t("{from} to {to}", { from: route[0].label, to: route[route.length - 1].label }));
    if (name === null) return;
    try {
      saved = await call<MapsState>(CHANNELS.mapsSaveRoute, { name, stops: route, mode: travel });
      flashToast(t("route saved"));
      paintSaved();
    } catch (error) {
      oops(error);
    }
  });
  byId("maps-clear-route").addEventListener("click", () => {
    stops = [];
    plan = null;
    keep();
    paintRoute();
  });

  listen<MapsState>(CHANNELS.mapsUpdated, (next) => {
    saved = next;
    paintSaved();
    paintResults();
  });
  onSharedChange("places", () => void loadShared());
  onViewShown("maps", async () => {
    if (!config?.ready) config = await call<MapsConfig>(CHANNELS.mapsConfig).catch(() => ({ ready: false, browserKey: "" }));
    paintSetup();
    if (!config.ready) return;
    saved = await call<MapsState>(CHANNELS.mapsGet).catch(() => saved);
    paintAll();
    void loadShared();
    await bootMap();
    paintPins();
  });
}
